import { useEffect, useState, type ReactNode } from "react";
import { useSession } from "../hooks";
import type { Attachment } from "../sync/types";
import { locale, t } from "../i18n";
import { Sheet } from "./ui";

export const inlineImages = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

function size(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Attachment links are signed per viewer and expire, so they are fetched
// when shown.
function useFileURL(id: string) {
  const session = useSession();
  const [url, setURL] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    session.fileURL(id).then((u) => live && setURL(u), () => {});
    return () => {
      live = false;
    };
  }, [id, session]);
  return url;
}

// viewImage asks the image viewer to open a message's image; a plain click
// opens it, a click with a modifier key opens the link as usual.
export type ViewImage = { fileID: string; messageID: string; channelID: string; threadRoot: string | null };

// Sent is who sent a message's files, and when, for the file sheet.
export type Sent = { author: string; at: string };

function AttachmentItem({ a, at, sent }: { a: Attachment; at?: Omit<ViewImage, "fileID">; sent?: Sent }) {
  const url = useFileURL(a.id);
  const [sheet, setSheet] = useState(false);
  if (inlineImages.has(a.mime)) {
    return url ? (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="image"
        onClick={(e) => {
          if (!at || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          dispatchEvent(new CustomEvent<ViewImage>("lantern:view-image", { detail: { fileID: a.id, ...at } }));
        }}
      >
        <img src={url} alt={a.name} loading="lazy" />
      </a>
    ) : (
      <span className="file">{a.name}</span>
    );
  }
  // A plain click shows the file before it is downloaded; one with a
  // modifier key downloads it at once, as links do.
  const link = (
    <>
      <a
        className="file"
        href={url ?? undefined}
        download={a.name}
        aria-haspopup="dialog"
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          setSheet(true);
        }}
      >
        📄 {a.name} <span className="size">{size(a.size)}</span>
      </a>
      {sheet && <FileSheet a={a} url={url} sent={sent} onClose={() => setSheet(false)} />}
    </>
  );
  if (a.mime.startsWith("video/")) {
    return (
      <figure className="media">
        {url && <video src={url} controls preload="metadata" aria-label={a.name} />}
        <figcaption>{link}</figcaption>
      </figure>
    );
  }
  if (a.mime.startsWith("audio/") || a.mime === "application/ogg") {
    return (
      <figure className="media audio">
        {url && <audio src={url} controls preload="metadata" aria-label={a.name} />}
        <figcaption>{link}</figcaption>
      </figure>
    );
  }
  if (isText(a)) return <TextPreview a={a} url={url} link={link} />;
  return link;
}

const textTypes = new Set(["application/json", "application/xml", "application/yaml", "application/x-yaml", "application/javascript", "application/x-sh", "application/toml"]);
const textNames = /\.(txt|md|log|csv|tsv|json|ya?ml|toml|ini|conf|xml|sh|sql|go|py|js|ts|tsx|jsx|java|kt|rs|c|h|cc|cpp|rb|php|css|diff|patch)$/i;

// isText says whether a file is text worth showing the start of: by type,
// or for one sent as plain bytes, by its name.
function isText(a: Attachment): boolean {
  return a.size > 0 && a.size <= 1 << 20 && (a.mime.startsWith("text/") || textTypes.has(a.mime) || (a.mime === "application/octet-stream" && textNames.test(a.name)));
}

const previewBytes = 16 << 10;
const previewLines = 12;

// TextPreview shows a text file's first lines, and all of its first 16 KB
// on request.
function TextPreview({ a, url, link }: { a: Attachment; url: string | null; link: ReactNode }) {
  const [text, setText] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!url) return;
    let live = true;
    fetch(url, { headers: { Range: `bytes=0-${previewBytes - 1}` } })
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(r.statusText))))
      .then((b) => live && setText(new TextDecoder("utf-8", { fatal: false }).decode(b)))
      .catch(() => live && setText(""));
    return () => {
      live = false;
    };
  }, [url]);
  const lines = (text ?? "").split("\n");
  const cut = !open && lines.length > previewLines;
  const more = a.size > previewBytes;
  return (
    <figure className="text-file">
      <figcaption>{link}</figcaption>
      {text && (
        <>
          <pre>{cut ? lines.slice(0, previewLines).join("\n") : text}</pre>
          {(cut || (open && lines.length > previewLines)) && (
            <button className="link" onClick={() => setOpen(!open)}>
              {open ? t("Show less") : t("Show more")}
            </button>
          )}
          {open && more && <span className="muted">{t("The rest is in the file.")}</span>}
        </>
      )}
    </figure>
  );
}

// kind is a file's type as people name it: its extension, or the kind of
// thing its type says.
function kind(a: Attachment): string {
  const ext = /\.([a-z0-9]{1,8})$/i.exec(a.name)?.[1];
  if (ext) return ext.toUpperCase();
  const sub = a.mime.split("/")[1];
  return sub && sub !== "octet-stream" ? sub.toUpperCase() : t("File");
}

// Where a phone keeps what it downloads, said where it is not obvious.
function downloadsHint(): string | null {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return t("On iPhone and iPad, downloads go to the Files app, in Downloads.");
  if (/Android/.test(ua)) return t("On Android, downloads go to the Downloads folder.");
  return null;
}

// FileSheet shows a file that is not shown in place: what it is, who sent
// it, and a button to download it.
function FileSheet({ a, url, sent, onClose }: { a: Attachment; url: string | null; sent?: Sent; onClose: () => void }) {
  const session = useSession();
  const hint = downloadsHint();
  return (
    <Sheet title={a.name} onClose={onClose} size="sm" className="page file-sheet">
      <div className="file-sheet-body">
        <span className="file-sheet-icon" aria-hidden>
          📄
        </span>
        <dl>
          <dt>{t("Type")}</dt>
          <dd>{kind(a)}</dd>
          <dt>{t("Size")}</dt>
          <dd>{size(a.size)}</dd>
          {sent && (
            <>
              <dt>{t("Sent by")}</dt>
              <dd>
                {session.name(sent.author)} · {new Date(sent.at).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })}
              </dd>
            </>
          )}
        </dl>
      </div>
      {hint && <p className="field-hint">{hint}</p>}
      <div className="form-actions">
        <button type="button" onClick={onClose}>
          {t("Close")}
        </button>
        {url ? (
          <a className="btn primary" href={url} download={a.name} onClick={() => setTimeout(onClose, 0)} data-autofocus>
            {t("Download")}
          </a>
        ) : (
          <button type="button" className="primary" disabled>
            {t("Download")}
          </button>
        )}
      </div>
    </Sheet>
  );
}

export function Attachments({ files, at, sent }: { files: Attachment[]; at?: Omit<ViewImage, "fileID">; sent?: Sent }) {
  return (
    <div className="attachments">
      {files.map((a) =>
        a.deleted ? (
          <span key={a.id} className="file deleted">
            {t("This file was deleted.")}
          </span>
        ) : (
          <AttachmentItem key={a.id} a={a} at={at} sent={sent} />
        ),
      )}
    </div>
  );
}
